import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ExternalLink, FileDown, Loader2, MessageSquarePlus, Send, Sparkles, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api } from '@/services/api';
import { useToast } from '@/components/ui/use-toast';
import { AiResultChart } from '@/components/ai/AiResultChart';
import { downloadAiResponseCsv } from '@/lib/ai-response-export';
import type {
  AiConversationMessage,
  AiEntityLink,
  AiQueryRequest,
  AiQueryResponse,
  AiScope,
} from '@/types';

interface AiAssistantPanelProps {
  title?: string;
  description?: string;
  initialScope?: AiScope;
  prisonerId?: string;
  caseId?: string;
  documentIds?: string[];
}

interface ChatTurn {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  response?: AiQueryResponse;
}

interface StoredChatSession {
  messages: ChatTurn[];
  scope: AiScope;
}

function getSessionStorageKey(prisonerId?: string) {
  return `egpa-ai-chat:${prisonerId || 'global'}`;
}

function loadStoredSession(storageKey: string): StoredChatSession | null {
  try {
    const raw = sessionStorage.getItem(storageKey);
    if (!raw) return null;
    return JSON.parse(raw) as StoredChatSession;
  } catch {
    return null;
  }
}

const scopeOptions: Array<{ value: AiScope; label: string }> = [
  { value: 'documents', label: 'Nur Dokumente' },
  { value: 'prisoners', label: 'Nur Gefangene & Stammdaten' },
  { value: 'both', label: 'Beides (automatisch erkennen)' },
];

function createId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

const PRISONER_PREVIEW_ROWS = 25;
const DOCUMENT_PREVIEW_ROWS = 8;

function buildPrisonerRows(response?: AiQueryResponse) {
  if (!response) return [];

  if (response.prisonerResults?.length) {
    return response.prisonerResults.map((row) => ({
      id: row.id,
      prisonerNumber: row.prisonerNumber || '-',
      displayName: row.displayName,
      wohnort: row.wohnort || '-',
      geburtsdatum: row.geburtsdatum ? row.geburtsdatum.slice(0, 10) : '-',
      geburtsort: (row.meta?.birthPlace as string | undefined) || '-',
      entlassdatum: row.entlassdatum ? row.entlassdatum.slice(0, 10) : '-',
      anstaltAbteilung: [row.anstalt, row.abteilung].filter(Boolean).join(' / ') || '-',
      dokumente:
        typeof row.meta?.documentCount === 'number' ? String(row.meta.documentCount) : '-',
    }));
  }

  if (response.structuredResult?.length) {
    return response.structuredResult.map((row) => ({
      id: row.id,
      prisonerNumber: row.prisonerNumber || '-',
      displayName: `${row.lastName}, ${row.firstName}`,
      wohnort: row.city || '-',
      geburtsdatum: row.dateOfBirth ? row.dateOfBirth.slice(0, 10) : '-',
      geburtsort: '-',
      entlassdatum: row.releaseDate
        ? row.releaseDate.slice(0, 10)
        : row.expectedEnd?.slice(0, 10) || '-',
      anstaltAbteilung: '-',
      dokumente: '-',
    }));
  }

  return [];
}

function EntityLinks({
  links,
  onNavigate,
}: {
  links: AiEntityLink[];
  onNavigate: (path: string) => void;
}) {
  if (!links.length) return null;

  const typeLabels: Record<AiEntityLink['type'], string> = {
    prisoner: 'Gefangene/r',
    document: 'Dokument',
    recordItem: 'Akteneintrag',
  };

  return (
    <div className="rounded-md border border-primary/20 bg-primary/5 p-3">
      <p className="mb-2 text-xs font-medium text-foreground">Direkt zum Datensatz</p>
      <div className="flex flex-col gap-2">
        {links.map((link) => (
          <Button
            key={`${link.type}-${link.id}`}
            variant="outline"
            size="sm"
            className="h-auto justify-start whitespace-normal px-3 py-2 text-left"
            onClick={() => onNavigate(link.path)}
          >
            <ExternalLink className="mr-2 h-4 w-4 shrink-0" />
            <span className="min-w-0">
              <span className="block text-[11px] uppercase tracking-wide text-muted-foreground">
                {typeLabels[link.type]}
              </span>
              <span className="block font-medium">{link.label}</span>
              {link.description && (
                <span className="block text-xs text-muted-foreground">{link.description}</span>
              )}
            </span>
          </Button>
        ))}
      </div>
    </div>
  );
}

function AssistantMessageBody({
  response,
  onNavigate,
  onExportCsv,
}: {
  response: AiQueryResponse;
  onNavigate: (path: string) => void;
  onExportCsv: () => void;
}) {
  const [showAllPrisoners, setShowAllPrisoners] = useState(false);
  const [showAllDocuments, setShowAllDocuments] = useState(false);
  const prisonerRows = useMemo(() => buildPrisonerRows(response), [response]);
  const documentRows = response.documentResults || [];
  const totalPrisonerCount =
    typeof response.analysisMeta?.totalCount === 'number'
      ? response.analysisMeta.totalCount
      : prisonerRows.length;
  const visiblePrisonerRows = showAllPrisoners
    ? prisonerRows
    : prisonerRows.slice(0, PRISONER_PREVIEW_ROWS);
  const hiddenPrisonerRows = showAllPrisoners
    ? 0
    : Math.max(0, prisonerRows.length - PRISONER_PREVIEW_ROWS);
  const unloadedPrisoners = Math.max(0, totalPrisonerCount - prisonerRows.length);
  const visibleDocumentRows = showAllDocuments
    ? documentRows
    : documentRows.slice(0, DOCUMENT_PREVIEW_ROWS);
  const hiddenDocumentRows = showAllDocuments
    ? 0
    : Math.max(0, documentRows.length - DOCUMENT_PREVIEW_ROWS);

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={onExportCsv}>
          <FileDown className="mr-2 h-4 w-4" />
          Als CSV exportieren
        </Button>
      </div>

      {!!response.entityLinks?.length && (
        <EntityLinks links={response.entityLinks} onNavigate={onNavigate} />
      )}

      {response.visualization && (
        <AiResultChart
          title={response.visualization.title}
          chartType={response.visualization.chartType}
          data={response.visualization.data}
        />
      )}

      {response.analysisMeta && (
        <div className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
          {typeof response.analysisMeta.totalCount === 'number' && (
            <p>Trefferzahl: {response.analysisMeta.totalCount}</p>
          )}
          {!!response.analysisMeta.appliedRules?.length && (
            <p className="mt-1">Regeln: {response.analysisMeta.appliedRules.join(' · ')}</p>
          )}
        </div>
      )}

      {!!prisonerRows.length && (
        <div className="overflow-x-auto rounded-md border">
          <div
            className={
              showAllPrisoners && prisonerRows.length > PRISONER_PREVIEW_ROWS
                ? 'max-h-[min(60vh,520px)] overflow-y-auto'
                : undefined
            }
          >
            <table className="w-full text-xs">
              <thead className="sticky top-0 z-10 bg-muted/40">
                <tr>
                  <th className="px-2 py-1 text-left">Nr.</th>
                  <th className="px-2 py-1 text-left">Name</th>
                  <th className="px-2 py-1 text-left">Wohnort</th>
                  <th className="px-2 py-1 text-left">Geburtsdatum</th>
                  <th className="px-2 py-1 text-left">Geburtsort</th>
                  <th className="px-2 py-1 text-left">Entlassung</th>
                  <th className="px-2 py-1 text-left">Dok.</th>
                </tr>
              </thead>
              <tbody>
                {visiblePrisonerRows.map((row) => (
                  <tr
                    key={row.id}
                    className="cursor-pointer border-t hover:bg-muted/30"
                    onClick={() => onNavigate(`/prisoners/${row.id}`)}
                  >
                    <td className="px-2 py-1 font-mono">{row.prisonerNumber}</td>
                    <td className="px-2 py-1">{row.displayName}</td>
                    <td className="px-2 py-1">{row.wohnort}</td>
                    <td className="px-2 py-1">{row.geburtsdatum}</td>
                    <td className="px-2 py-1">{row.geburtsort}</td>
                    <td className="px-2 py-1">{row.entlassdatum}</td>
                    <td className="px-2 py-1">{row.dokumente}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {hiddenPrisonerRows > 0 && (
            <button
              type="button"
              className="w-full px-2 py-2 text-xs text-primary hover:bg-muted/30 hover:underline"
              onClick={() => setShowAllPrisoners(true)}
            >
              + {hiddenPrisonerRows} weitere Treffer anzeigen
            </button>
          )}
          {showAllPrisoners && prisonerRows.length > PRISONER_PREVIEW_ROWS && (
            <button
              type="button"
              className="w-full border-t px-2 py-2 text-xs text-muted-foreground hover:bg-muted/30 hover:underline"
              onClick={() => setShowAllPrisoners(false)}
            >
              Weniger anzeigen
            </button>
          )}
          {unloadedPrisoners > 0 && (
            <p className="border-t px-2 py-1 text-xs text-muted-foreground">
              {unloadedPrisoners} weitere Treffer sind nicht geladen (Anzeigelimit). Nutzen Sie den CSV-Export
              für die vollständige Auswertung.
            </p>
          )}
        </div>
      )}

      {!!documentRows.length && (
        <div className="overflow-x-auto rounded-md border">
          <div
            className={
              showAllDocuments && documentRows.length > DOCUMENT_PREVIEW_ROWS
                ? 'max-h-[min(50vh,420px)] overflow-y-auto'
                : undefined
            }
          >
            <table className="w-full text-xs">
              <thead className="sticky top-0 z-10 bg-muted/40">
                <tr>
                  <th className="px-2 py-1 text-left">Datei</th>
                  <th className="px-2 py-1 text-left">Gefangene/r</th>
                  <th className="px-2 py-1 text-left">Akteneintrag</th>
                  <th className="px-2 py-1 text-left">Typ</th>
                  <th className="px-2 py-1 text-left">OCR</th>
                </tr>
              </thead>
              <tbody>
                {visibleDocumentRows.map((row) => (
                  <tr
                    key={row.id}
                    className="cursor-pointer border-t hover:bg-muted/30"
                    onClick={() => onNavigate(`/prisoners/${row.prisonerId}?view=documents&documentId=${row.id}`)}
                  >
                    <td className="px-2 py-1">{row.filename}</td>
                    <td className="px-2 py-1">{row.prisonerName || row.prisonerNumber || '-'}</td>
                    <td className="px-2 py-1">{row.recordItemTitle || '-'}</td>
                    <td className="px-2 py-1">{row.recordItemType || '-'}</td>
                    <td className="px-2 py-1">{row.ocrStatus || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {hiddenDocumentRows > 0 && (
            <button
              type="button"
              className="w-full px-2 py-2 text-xs text-primary hover:bg-muted/30 hover:underline"
              onClick={() => setShowAllDocuments(true)}
            >
              + {hiddenDocumentRows} weitere Dokumente anzeigen
            </button>
          )}
          {showAllDocuments && documentRows.length > DOCUMENT_PREVIEW_ROWS && (
            <button
              type="button"
              className="w-full border-t px-2 py-2 text-xs text-muted-foreground hover:bg-muted/30 hover:underline"
              onClick={() => setShowAllDocuments(false)}
            >
              Weniger anzeigen
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export function AiAssistantPanel({
  title = 'KI-Assistent',
  description = 'Durchsuche Stammdaten, Dokumente und Zählungen per natürlicher Sprache – mit Tabellen und Grafiken.',
  initialScope = 'both',
  prisonerId,
  caseId,
  documentIds,
}: AiAssistantPanelProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const scrollRef = useRef<HTMLDivElement>(null);
  const storageKey = getSessionStorageKey(prisonerId);
  const [scope, setScope] = useState<AiScope>(initialScope);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<ChatTurn[]>([]);
  const [sessionReady, setSessionReady] = useState(false);

  const { data: contextPrisoner } = useQuery({
    queryKey: ['prisoner', prisonerId],
    queryFn: () => api.getPrisoner(prisonerId!),
    enabled: Boolean(prisonerId),
  });

  const contextDescription = useMemo(() => {
    if (!contextPrisoner) {
      return description;
    }
    return (
      `${description} Aktiver Datensatz: ${contextPrisoner.lastName}, ${contextPrisoner.firstName} ` +
      `(${contextPrisoner.prisonerNumber}). Fragen beziehen sich automatisch auf diese Akte.`
    );
  }, [contextPrisoner, description]);

  useEffect(() => {
    const stored = loadStoredSession(storageKey);
    if (stored?.messages?.length) {
      setMessages(stored.messages);
      setScope(stored.scope || initialScope);
    }
    setSessionReady(true);
  }, [storageKey, initialScope]);

  useEffect(() => {
    if (!sessionReady) return;

    if (!messages.length) {
      sessionStorage.removeItem(storageKey);
      return;
    }

    const payload: StoredChatSession = { messages, scope };
    sessionStorage.setItem(storageKey, JSON.stringify(payload));
  }, [messages, scope, sessionReady, storageKey]);

  const submitMutation = useMutation({
    mutationFn: (payload: AiQueryRequest) => api.queryAi(payload),
    onSuccess: (data, variables) => {
      setMessages((current) => [
        ...current,
        {
          id: createId(),
          role: 'assistant',
          content: data.answer,
          response: data,
        },
      ]);
    },
    onError: (error: Error) => {
      setMessages((current) => [
        ...current,
        {
          id: createId(),
          role: 'assistant',
          content:
            'Die KI-Anfrage ist fehlgeschlagen. Bitte erneut versuchen oder den Suchumfang auf „Nur Gefangene & Stammdaten“ setzen.\n\n' +
            `Details: ${error.message}`,
        },
      ]);
      toast({
        title: 'KI-Abfrage fehlgeschlagen',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, submitMutation.isPending]);

  const buildConversationHistory = (currentMessages: ChatTurn[]): AiConversationMessage[] =>
    currentMessages.map((message) => ({
      role: message.role,
      content: message.content,
    }));

  const handleSubmit = () => {
    const question = input.trim();
    if (!question) {
      toast({
        title: 'Bitte Frage eingeben',
        description: 'Gib eine Frage oder Nachfrage ein.',
        variant: 'destructive',
      });
      return;
    }

    const historyForRequest = buildConversationHistory(messages);

    setMessages((current) => [
      ...current,
      { id: createId(), role: 'user', content: question },
    ]);
    setInput('');

    submitMutation.mutate({
      question,
      scope,
      prisonerId,
      caseId,
      documentIds,
      conversationHistory: historyForRequest,
    });
  };

  const getQuestionForMessage = (messageIndex: number) => {
    for (let index = messageIndex - 1; index >= 0; index -= 1) {
      if (messages[index]?.role === 'user') {
        return messages[index].content;
      }
    }
    return undefined;
  };

  const handleExportCsv = (messageIndex: number, response: AiQueryResponse) => {
    const question = getQuestionForMessage(messageIndex);
    downloadAiResponseCsv(response, question);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (!submitMutation.isPending) {
        handleSubmit();
      }
    }
  };

  const resetChat = () => {
    setMessages([]);
    setInput('');
    sessionStorage.removeItem(storageKey);
  };

  return (
    <Card className="flex h-[min(78vh,820px)] flex-col">
      <CardHeader className="shrink-0 border-b pb-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              {title}
            </CardTitle>
            <CardDescription>{contextDescription}</CardDescription>
          </div>
          {messages.length > 0 && (
            <Button variant="outline" size="sm" onClick={resetChat}>
              <MessageSquarePlus className="mr-2 h-4 w-4" />
              Neues Gespräch
            </Button>
          )}
        </div>
        <div className="mt-3">
          <select
            value={scope}
            onChange={(event) => setScope(event.target.value as AiScope)}
            className="w-full rounded-md border bg-background px-3 py-2 text-sm md:max-w-sm"
          >
            {scopeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </CardHeader>

      <CardContent className="flex min-h-0 flex-1 flex-col p-0">
        <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto p-4">
          {messages.length === 0 && !submitMutation.isPending && (
            <div className="rounded-lg border border-dashed p-6 text-sm text-muted-foreground">
              <p className="font-medium text-foreground">Beispiele für Datensuche:</p>
              <ol className="mt-2 list-decimal space-y-1 pl-5">
                <li>„Wie viele aktive Gefangene wohnen in Hamburg?“</li>
                <li>„Wann ist Gefangene/r Müller geboren?“</li>
                <li>„Wie viele Dokumente hat Gefangene/r Schmidt?“</li>
                <li>„Liste alle Dokumente mit Verfügung im Titel“</li>
                <li>„Gruppiere Akteneinträge nach Typ“</li>
              </ol>
            </div>
          )}

          {messages.map((message, messageIndex) => (
            <div
              key={message.id}
              className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              {message.role === 'assistant' && (
                <div className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10">
                  <Sparkles className="h-4 w-4 text-primary" />
                </div>
              )}
              <div
                className={`max-w-[92%] rounded-2xl px-4 py-3 text-sm ${
                  message.role === 'user'
                    ? 'bg-primary text-primary-foreground'
                    : 'border bg-background'
                }`}
              >
                <p className="whitespace-pre-wrap">{message.content}</p>
                {message.role === 'assistant' && message.response && (
                  <div className="mt-3 border-t pt-3">
                    <AssistantMessageBody
                      response={message.response}
                      onNavigate={(path) => navigate(path)}
                      onExportCsv={() => handleExportCsv(messageIndex, message.response!)}
                    />
                  </div>
                )}
              </div>
              {message.role === 'user' && (
                <div className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted">
                  <User className="h-4 w-4" />
                </div>
              )}
            </div>
          ))}

          {submitMutation.isPending && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Antwort wird erstellt ...
            </div>
          )}
        </div>

        <div className="shrink-0 border-t p-4">
          <div className="flex gap-2">
            <Input
              value={input}
              onChange={(event) => setInput(event.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Frage stellen oder Nachfrage eingeben ..."
              disabled={submitMutation.isPending}
            />
            <Button onClick={handleSubmit} disabled={submitMutation.isPending}>
              {submitMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
